import type { FragmentsModel } from '@thatopen/fragments'
import { viewerApi } from '@viewer/lib/viewerApi'

// ============================================================================
// itemDimensions: shared classifier infrastructure
//
// Both the paint engine (Colorir por…) and the filter engine (Filtro)
// need to bucket items by an arbitrary "dimension": IFC class, storey,
// system, property, etc. This module exposes the classifiers + value
// enumeration so callers don't reimplement them.
//
// classColors.ts currently keeps its own copies of most of these for
// historical reasons; consolidation is fair game when next touching
// it.
// ============================================================================

export type DimensionMode =
  | 'entity'
  | 'predefinedType'
  | 'storey'
  | 'system'
  | 'material'
  | 'type'
  | 'property'

export interface DimensionCriterion {
  mode: DimensionMode
  /** Only set when mode === 'property'. Syntax: "Pset_X.Y" or "Name". */
  propertyKey?: string
}

// modelId -> all-items-by-category map (cached, geometric only)
const itemCategoryCache = new Map<string, Record<string, number[]>>()
// modelId -> set of localIds with geometry
const geometricSetCache = new Map<string, Set<number>>()

async function getGeometricIds(model: FragmentsModel): Promise<Set<number>> {
  const cached = geometricSetCache.get(model.modelId)
  if (cached) return cached
  let ids: number[] = []
  try {
    ids = await model.getItemsIdsWithGeometry()
  } catch (err) {
    console.warn('[itemDimensions] getItemsIdsWithGeometry failed', err)
  }
  const s = new Set(ids)
  geometricSetCache.set(model.modelId, s)
  return s
}

async function getItemsByCategory(
  model: FragmentsModel,
): Promise<Record<string, number[]>> {
  const cached = itemCategoryCache.get(model.modelId)
  if (cached) return cached
  let raw: Record<string, number[]> = {}
  try {
    raw = await model.getItemsOfCategories([/.*/])
  } catch (err) {
    console.warn('[itemDimensions] getItemsOfCategories failed', err)
  }
  const geometric = await getGeometricIds(model)
  const filtered: Record<string, number[]> = {}
  for (const [cat, ids] of Object.entries(raw)) {
    if (!ids || ids.length === 0) continue
    const onlyGeo =
      geometric.size > 0 ? ids.filter((id) => geometric.has(id)) : ids
    if (onlyGeo.length > 0) filtered[cat] = onlyGeo
  }
  itemCategoryCache.set(model.modelId, filtered)
  return filtered
}

function readAttrValue(item: unknown, name: string): string | null {
  if (!item || typeof item !== 'object') return null
  const v = (item as Record<string, unknown>)[name]
  if (v == null) return null
  if (typeof v === 'object' && 'value' in (v as object)) {
    const raw = (v as { value: unknown }).value
    if (raw == null || raw === '') return null
    return String(raw)
  }
  if (v === '') return null
  return String(v)
}

/** Items per `getItemsData` call while classifying. Small enough that
 *  the extraction loop of one batch never holds the main thread for
 *  long; large enough to amortise the round-trip to the Fragments
 *  worker. */
const EXTRACTION_BATCH = 2000

/** Yield to the event loop between batches so the UI keeps painting
 *  and reacting while a big model is classified. */
function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0))
}

/** Visit EVERY geometric item of the model with its data, in batches.
 *
 *  It used to read only the first 10 000 items (a sample meant for the
 *  value dropdowns), and the same sample fed the Filter and the clash
 *  rule sets: on a bigger model a property rule silently matched only
 *  part of the elements. Now every item is read, batch by batch, with
 *  a yield in between, and only one batch of item data is alive at a
 *  time. */
async function forEachExtractedItem(
  model: FragmentsModel,
  wantsRelations: boolean,
  visit: (id: number, item: unknown) => void,
): Promise<void> {
  const bucket = await getItemsByCategory(model)
  const allIds: number[] = []
  for (const ids of Object.values(bucket)) {
    if (ids) allIds.push(...ids)
  }
  for (let start = 0; start < allIds.length; start += EXTRACTION_BATCH) {
    if (start > 0) await yieldToEventLoop()
    const ids = allIds.slice(start, start + EXTRACTION_BATCH)
    let items: unknown[]
    try {
      items = await model.getItemsData(ids, {
        attributesDefault: true,
        relationsDefault: { attributes: true, relations: wantsRelations },
      })
    } catch (err) {
      console.warn('[itemDimensions] getItemsData failed', err)
      continue
    }
    ids.forEach((id, idx) => visit(id, items[idx]))
  }
}

async function classifyByEntity(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  const bucket = await getItemsByCategory(model)
  for (const [cat, ids] of Object.entries(bucket)) {
    if (!ids) continue
    for (const id of ids) out.set(id, cat)
  }
  return out
}

async function classifyByPredefinedType(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  await forEachExtractedItem(model, false, (id, rawItem) => {
    const item = rawItem
    const value =
      readAttrValue(item, 'PredefinedType') ??
      readAttrValue(item, 'ObjectType')
    if (value && value !== 'NOTDEFINED' && value !== 'USERDEFINED') {
      out.set(id, value)
    }
  })
  return out
}

async function classifyBySystem(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()

  // 1) Find every group/system/zone in the model via Fragments (so we
  //    stay in localId space throughout; rawIfc returns express IDs
  //    which would be silently wrong for selection downstream).
  const GROUP_REGEXES = [
    /^IFCSYSTEM$/i,
    /^IFCDISTRIBUTIONSYSTEM$/i,
    /^IFCBUILDINGSYSTEM$/i,
    /^IFCGROUP$/i,
    /^IFCZONE$/i,
  ]
  let groupBuckets: Record<string, number[]>
  try {
    groupBuckets = await model.getItemsOfCategories(GROUP_REGEXES)
  } catch (err) {
    console.warn('[itemDimensions] classifyBySystem: groups query failed', err)
    return out
  }
  const groupIds: number[] = []
  for (const ids of Object.values(groupBuckets)) {
    if (ids) groupIds.push(...ids)
  }
  if (groupIds.length === 0) return out

  // 2) For each group, fetch its Name / category AND its IsGroupedBy
  //    relation (which carries RelatedObjects = the members). Members
  //    come back as localId-keyed objects; we extract the localId for
  //    each.
  let groupData: unknown[] = []
  try {
    groupData = await model.getItemsData(groupIds, {
      attributesDefault: true,
      relationsDefault: { attributes: true, relations: false },
    })
  } catch (err) {
    console.warn(
      '[itemDimensions] classifyBySystem: groups getItemsData failed',
      err,
    )
    return out
  }
  groupIds.forEach((groupId, i) => {
    const group = groupData[i] as Record<string, unknown> | null
    if (!group) return
    const name =
      readAttrValue(group, 'Name') ??
      readAttrValue(group, 'ObjectType') ??
      readAttrValue(group, 'LongName') ??
      readAttrValue(group, 'Description')
    const category =
      readAttrValue(group, '_category') ??
      readAttrValue(group, 'category') ??
      'IfcGroup'
    const label = name ? `${name} (${category})` : `${category} #${groupId}`

    // IsGroupedBy is the inverse on IfcGroup pointing at the
    // IfcRelAssignsToGroup rels. Each rel has a RelatedObjects array
    // whose entries are the members. Different Fragments versions
    // serialise the localId field as `localId` or `_localId`; we try
    // both.
    const rels = group.IsGroupedBy as unknown[] | undefined
    if (!Array.isArray(rels)) return
    for (const rel of rels) {
      if (!rel || typeof rel !== 'object') continue
      const related = (rel as Record<string, unknown>).RelatedObjects as
        | unknown[]
        | undefined
      if (!Array.isArray(related)) continue
      for (const m of related) {
        if (typeof m === 'number') {
          out.set(m, label)
          continue
        }
        if (!m || typeof m !== 'object') continue
        const mo = m as Record<string, unknown>
        const memberId =
          (typeof mo.localId === 'number' ? mo.localId : null) ??
          (typeof mo._localId === 'number' ? mo._localId : null) ??
          (typeof mo.value === 'number' ? mo.value : null)
        if (typeof memberId === 'number') out.set(memberId, label)
      }
    }
  })
  return out
}

function extractMaterialLabel(target: unknown): string | null {
  if (!target || typeof target !== 'object') return null
  const t = target as Record<string, unknown>
  const directName = readAttrValue(t, 'Name')
  if (directName) return directName
  const matList = t.Materials as unknown[] | undefined
  if (Array.isArray(matList) && matList.length > 0) {
    const names: string[] = []
    for (const m of matList) {
      const n = readAttrValue(m, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  const lsUsage = t.ForLayerSet as Record<string, unknown> | undefined
  const layerSet = lsUsage ?? (t.MaterialLayers ? t : undefined)
  if (layerSet) {
    const layers = (layerSet.MaterialLayers ?? layerSet.Layers) as
      | unknown[]
      | undefined
    if (Array.isArray(layers)) {
      const names: string[] = []
      for (const l of layers) {
        if (!l || typeof l !== 'object') continue
        const mat = (l as Record<string, unknown>).Material
        const n = readAttrValue(mat, 'Name')
        if (n) names.push(n)
      }
      if (names.length > 0) return names.join(' / ')
    }
  }
  const constituents = t.MaterialConstituents as unknown[] | undefined
  if (Array.isArray(constituents) && constituents.length > 0) {
    const names: string[] = []
    for (const c of constituents) {
      if (!c || typeof c !== 'object') continue
      const mat = (c as Record<string, unknown>).Material
      const n = readAttrValue(mat, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  const profiles = t.MaterialProfiles as unknown[] | undefined
  if (Array.isArray(profiles) && profiles.length > 0) {
    const names: string[] = []
    for (const p of profiles) {
      if (!p || typeof p !== 'object') continue
      const mat = (p as Record<string, unknown>).Material
      const n = readAttrValue(mat, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  return null
}

async function classifyByMaterial(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  await forEachExtractedItem(model, true, (id, rawItem) => {
    const item = rawItem as Record<string, unknown> | null
    if (!item) return
    const rels = item.HasAssociations as unknown[] | undefined
    if (!Array.isArray(rels) || rels.length === 0) return
    for (const r of rels) {
      if (!r || typeof r !== 'object') continue
      const ro = r as Record<string, unknown>
      const ifcType = (ro.type ?? ro.expressType ?? '') as string
      const isMaterialRel =
        typeof ifcType === 'string' &&
        ifcType.toUpperCase().includes('IFCRELASSOCIATESMATERIAL')
      const rm = ro.RelatingMaterial
      const target = isMaterialRel || rm ? rm : null
      if (!target || typeof target !== 'object') continue
      const label = extractMaterialLabel(target)
      if (label) {
        out.set(id, label)
        break
      }
    }
  })
  return out
}

async function classifyByType(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  await forEachExtractedItem(model, true, (id, rawItem) => {
    const item = rawItem as Record<string, unknown> | null
    if (!item) return
    const rels = item.IsTypedBy as unknown[] | undefined
    if (!Array.isArray(rels) || rels.length === 0) return
    for (const r of rels) {
      if (!r || typeof r !== 'object') continue
      const rt = (r as Record<string, unknown>).RelatingType
      const name =
        rt && typeof rt === 'object' && 'Name' in (rt as object)
          ? readAttrValue(rt, 'Name')
          : null
      if (name) {
        out.set(id, name)
        break
      }
    }
  })
  return out
}

async function classifyByProperty(
  model: FragmentsModel,
  key: string,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  const target = key.trim()
  if (!target) return out
  const dotIdx = target.indexOf('.')
  const psetName = dotIdx >= 0 ? target.slice(0, dotIdx) : null
  const propName = dotIdx >= 0 ? target.slice(dotIdx + 1) : target
  const wantsRelations = psetName !== null
  await forEachExtractedItem(model, wantsRelations, (id, rawItem) => {
    const item = rawItem as Record<string, unknown> | null
    if (!item) return
    let value: string | null = null
    if (psetName) {
      const rels = item.IsDefinedBy as unknown[] | undefined
      if (Array.isArray(rels)) {
        for (const r of rels) {
          if (!r || typeof r !== 'object') continue
          const ro = r as Record<string, unknown>
          const nm = readAttrValue(ro, 'Name')
          if (nm !== psetName) continue
          // IfcPropertySet -> HasProperties[].NominalValue;
          // IfcElementQuantity -> Quantities[].<Length|Area|...>Value
          // (same shapes ifcItemData.extractPsetRows reads). Without
          // the second branch a "Qto_*.Length > x" rule never matched.
          const props = ro.HasProperties as unknown[] | undefined
          if (Array.isArray(props)) {
            for (const p of props) {
              if (!p || typeof p !== 'object') continue
              const po = p as Record<string, unknown>
              if (readAttrValue(po, 'Name') !== propName) continue
              const nv = po.NominalValue
              if (nv && typeof nv === 'object' && 'value' in (nv as object)) {
                const raw = (nv as { value: unknown }).value
                if (raw != null && raw !== '') value = String(raw)
              }
            }
          }
          const quantities = ro.Quantities as unknown[] | undefined
          if (Array.isArray(quantities)) {
            for (const q of quantities) {
              if (!q || typeof q !== 'object') continue
              const qo = q as Record<string, unknown>
              if (readAttrValue(qo, 'Name') !== propName) continue
              const qv =
                readAttrValue(qo, 'LengthValue') ??
                readAttrValue(qo, 'AreaValue') ??
                readAttrValue(qo, 'VolumeValue') ??
                readAttrValue(qo, 'CountValue') ??
                readAttrValue(qo, 'WeightValue') ??
                readAttrValue(qo, 'TimeValue')
              if (qv !== null) value = qv
            }
          }
        }
      }
    } else {
      value = readAttrValue(item, propName)
    }
    if (value !== null) out.set(id, value)
  })
  return out
}

async function classifyByStorey(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  // Walk each item's spatial ancestors to find its IfcBuildingStorey.
  // Storey names come from getItemsData on the storey ids themselves.
  const out = new Map<number, string>()
  const bucket = await getItemsByCategory(model)
  // BuildingStorey items have geometry (the bbox empty volume), so they
  // appear in the geometric category bucket; if not, also check the raw
  // category map by querying directly.
  let storeys = bucket['IFCBUILDINGSTOREY'] ?? []
  if (storeys.length === 0) {
    try {
      const r = await model.getItemsOfCategories([/^IFCBUILDINGSTOREY$/i])
      storeys = r['IFCBUILDINGSTOREY'] ?? Object.values(r)[0] ?? []
    } catch {
      /* ignore */
    }
  }
  if (storeys.length === 0) return out
  let storeyData: unknown[] = []
  try {
    storeyData = await model.getItemsData(storeys, {
      attributesDefault: true,
      relationsDefault: { attributes: false, relations: false },
    })
  } catch (err) {
    console.warn('[itemDimensions] storey getItemsData failed', err)
    return out
  }
  const storeyName = new Map<number, string>()
  storeys.forEach((id, i) => {
    const data = storeyData[i] as Record<string, unknown> | null
    const name = readAttrValue(data, 'Name') ?? `#${id}`
    storeyName.set(id, name)
  })
  // For each geometric item, walk its ancestor path to find a storey.
  const geometric = await getGeometricIds(model)
  for (const localId of geometric) {
    const path = viewerApi.getAncestorPath(model.modelId, localId)
    for (const ancestorId of path) {
      const name = storeyName.get(ancestorId)
      if (name) {
        out.set(localId, name)
        break
      }
    }
  }
  return out
}

/** Classify every item of `model` by `criterion`. Returns Map<localId,
 *  bucketKey>. Items not matching the criterion are absent from the
 *  map. */
export async function classifyDimension(
  model: FragmentsModel,
  criterion: DimensionCriterion,
): Promise<Map<number, string>> {
  switch (criterion.mode) {
    case 'entity':
      return classifyByEntity(model)
    case 'predefinedType':
      return classifyByPredefinedType(model)
    case 'storey':
      return classifyByStorey(model)
    case 'system':
      return classifyBySystem(model)
    case 'material':
      return classifyByMaterial(model)
    case 'type':
      return classifyByType(model)
    case 'property':
      return classifyByProperty(model, criterion.propertyKey ?? '')
  }
}

/** Return the sorted list of distinct values that `criterion` produces
 *  across all `models`. Used by the filter panel to populate its value
 *  dropdowns. */
export async function getDimensionDistinctValues(
  models: FragmentsModel[],
  criterion: DimensionCriterion,
): Promise<string[]> {
  const values = new Set<string>()
  for (const model of models) {
    const map = await classifyDimension(model, criterion)
    for (const v of map.values()) values.add(v)
  }
  return Array.from(values).sort()
}

/** Drop cached state for a model that's about to be unloaded. Mirrors
 *  the same hook in classColors.ts / paintEngine.ts. */
export function forgetItemDimensions(modelId: string): void {
  itemCategoryCache.delete(modelId)
  geometricSetCache.delete(modelId)
}
